var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useCapsuleStore, useModalStore, useUserInfoStore } from '../../stores/index.js';
import { ModalStep } from '../../utils/steps.js';
import { Header } from '../Header/Header.js';
import { Body } from '../Body/Body.js';
import { Footer } from '../Footer/Footer.js';
import { DEFAULTS } from '../../constants/defaults.js';
import { CpslAnimation } from '@usecapsule/react-components';
import { useGoBack } from '../../hooks/useGoBack.js';
export const ModalContent = forwardRef(({ twoFactorAuthEnabled = false, oAuthMethods, currentStepOverride, hasFinishedAnimation, disableEmailLogin, disablePhoneLogin, networks = ['ETHEREUM'], onClose, onRampConfig, loginTransitionOverride, createWalletOverride, setModalExpanded, }, ref) => {
    const capsule = useCapsuleStore(state => state.capsule);
    const currentStep = useModalStore(state => state.step);
    const webAuthURLForLogin = useModalStore(state => state.webAuthURLForLogin);
    const webAuthURLForCreate = useModalStore(state => state.webAuthURLForCreate);
    const isLogin = useModalStore(state => state.isLogin());
    const loginWindow = useModalStore(state => state.loginWindow);
    const setStep = useModalStore(state => state.setStep);
    const setWebAuthURLForLogin = useModalStore(state => state.setWebAuthURLForLogin);
    const setWebAuthURLForCreate = useModalStore(state => state.setWebAuthURLForCreate);
    const resetModalState = useModalStore(state => state.resetState);
    const setLoginWindow = useModalStore(state => state.setLoginWindow);
    const resetUserInfoState = useUserInfoStore(state => state.resetState);
    const goBack = useGoBack();
    const loginTimeout = useRef();
    const createAccountTimeout = useRef();
    const [walletCreationInProgress, setWalletCreationInProgress] = useState(false);
    const [recoveryShare, setRecoveryShare] = useState(null);
    useImperativeHandle(ref, () => {
        return {
            handleModalClose() {
                handleClose();
            },
        };
    }, []);
    const is2FASetup = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!twoFactorAuthEnabled) {
            return true;
        }
        try {
            const { isSetup } = yield capsule.check2FAStatus();
            return isSetup;
        }
        catch (error) {
            console.error('An error occurred while checking 2FA:', error);
            return false;
        }
    });
    function awaitLoginTransition() {
        return __awaiter(this, void 0, void 0, function* () {
            const { isComplete, isError, needsWallet } = yield capsule.waitForLoginAndSetup(loginWindow);
            setLoginWindow(undefined);
            if (isError) {
                goBack();
                return;
            }
            if (isComplete) {
                setWebAuthURLForLogin('');
                if (needsWallet) {
                    setStep(ModalStep.AWAITING_WALLET_CREATION);
                }
                else {
                    if (yield is2FASetup()) {
                        setStep(ModalStep.LOGIN_DONE);
                    }
                    else {
                        setStep(ModalStep.SETUP_2FA);
                    }
                }
            }
        });
    }
    function awaitWalletCreationTransition() {
        return __awaiter(this, void 0, void 0, function* () {
            const isComplete = yield capsule.waitForAccountCreation();
            if (isComplete) {
                setWebAuthURLForCreate('');
                setStep(ModalStep.AWAITING_WALLET_CREATION);
            }
        });
    }
    useEffect(() => {
        if (currentStepOverride) {
            setStep(currentStepOverride);
        }
    }, [currentStepOverride]);
    // generate/claim wallet once we know it's account creation
    useEffect(() => {
        if (currentStep !== ModalStep.AWAITING_WALLET_CREATION || walletCreationInProgress) {
            return;
        }
        function genWallet() {
            return __awaiter(this, void 0, void 0, function* () {
                setWalletCreationInProgress(true);
                let recoverySecret, walletIds;
                if (!createWalletOverride) {
                    const created = yield capsule.waitForPasskeyAndCreateWallet();
                    recoverySecret = created.recoverySecret;
                    walletIds = created.walletIds;
                }
                else {
                    const created = yield createWalletOverride(capsule);
                    const fetchedWallets = (yield capsule.fetchWallets()).filter(wallet => !!wallet.address);
                    const newWallets = {};
                    for (const wallet of fetchedWallets) {
                        newWallets[wallet.id] = {
                            id: wallet.id,
                            address: wallet.address,
                            scheme: wallet.scheme,
                            signer: '',
                        };
                    }
                    capsule.setWallets(newWallets);
                    recoverySecret = created.recoverySecret;
                    walletIds = created.walletIds;
                }
                yield capsule.setCurrentWalletIds(walletIds);
                setRecoveryShare(recoverySecret);
                setWalletCreationInProgress(false);
                if (!recoverySecret) {
                    setStep(ModalStep.WALLET_CREATION_DONE);
                }
                else {
                    setStep(ModalStep.SECRET);
                }
            });
        }
        genWallet();
    }, [isLogin, currentStep]);
    // wait for biometric to be added to move on to next step
    useEffect(() => {
        if (webAuthURLForCreate) {
            createAccountTimeout.current = window.setTimeout(awaitWalletCreationTransition, DEFAULTS.POLLING_INTERVAL_MS);
        }
        return () => clearTimeout(createAccountTimeout.current);
    }, [webAuthURLForCreate]);
    // wait for login auth to do post login setup
    useEffect(() => {
        if (webAuthURLForLogin && loginWindow) {
            if (loginTransitionOverride) {
                function loginOverride() {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield loginTransitionOverride(capsule);
                        setWebAuthURLForLogin('');
                        if (yield is2FASetup()) {
                            setStep(ModalStep.LOGIN_DONE);
                        }
                        else {
                            setStep(ModalStep.SETUP_2FA);
                        }
                    });
                }
                loginOverride();
                return;
            }
            loginTimeout.current = window.setTimeout(awaitLoginTransition, DEFAULTS.POLLING_INTERVAL_MS);
        }
        return () => window.clearTimeout(loginTimeout.current);
    }, [webAuthURLForLogin, loginWindow]);
    const handleClose = () => {
        if (currentStep === ModalStep.LOGIN_DONE ||
            currentStep === ModalStep.TWO_FACTOR_DONE ||
            currentStep === ModalStep.SETUP_2FA ||
            currentStep === ModalStep.SECRET ||
            currentStep === ModalStep.BIOMETRIC_LOGIN ||
            currentStep === ModalStep.BIOMETRIC_CREATION ||
            currentStep === ModalStep.WALLET_CREATION_DONE) {
            // Using a timeout here so state is reset once modal animates out.
            setTimeout(() => {
                resetModalState();
                resetUserInfoState();
                setRecoveryShare(null);
            }, 200);
        }
        else if (currentStep === ModalStep.ADD_FUNDS ||
            currentStep === ModalStep.ADD_FUNDS_AWAITING ||
            currentStep === ModalStep.ADD_FUNDS_SUCCESS ||
            currentStep === ModalStep.ADD_FUNDS_FAILURE) {
            setTimeout(() => {
                setStep(ModalStep.LOGIN_DONE);
            }, 200);
        }
        else {
            setStep(ModalStep.SIGN_UP);
        }
        capsule.exitLoops();
        onClose();
    };
    useEffect(() => {
        if (![ModalStep.BIOMETRIC_CREATION, ModalStep.AWAITING_BIOMETRIC_CREATION].includes(currentStep)) {
            capsule.exitAccountCreation();
        }
        if (![ModalStep.BIOMETRIC_LOGIN, ModalStep.AWAITING_BIOMETRIC_LOGIN].includes(currentStep)) {
            capsule.exitLogin();
        }
    }, [currentStep]);
    useEffect(() => {
        return () => {
            capsule.exitLoops();
        };
    }, []);
    const handleExpandModal = () => {
        setModalExpanded(true);
    };
    const handleCondenseModal = () => {
        setModalExpanded(false);
    };
    return (_jsxs(_Fragment, { children: [[ModalStep.WALLET_CREATION_DONE, ModalStep.ADD_FUNDS_SUCCESS].includes(currentStep) && (_jsx(CpslAnimation, { src: "https://product-assets.sandbox.usecapsule.com/animations/confetti.json" })), _jsx(Header, { onClose: handleClose, condenseModal: handleCondenseModal }), _jsx(Body, { hasFinishedAnimation: hasFinishedAnimation, oAuthMethods: oAuthMethods, twoFactorAuthEnabled: twoFactorAuthEnabled, recoveryShare: recoveryShare, disableEmailLogin: disableEmailLogin, disablePhoneLogin: disablePhoneLogin, networks: networks, onClose: handleClose, onRampConfig: onRampConfig }), _jsx(Footer, { expandModal: handleExpandModal })] }));
});
